import { lazy } from 'react';

/**
 * Wrapper around React.lazy that handles stale chunk errors after deploys.
 *
 * When Vite deploys a new build, old hashed chunk filenames are gone.
 * Users with the old app in memory will get "Failed to fetch dynamically
 * imported module" errors. This wrapper detects that and forces a hard reload
 * so the browser fetches the new index.html and fresh chunks.
 *
 * We track reloads in sessionStorage to avoid infinite reload loops in case
 * there's a genuine 404 that won't be fixed by reloading.
 */

const RELOAD_FLAG = 'fyso_chunk_reload';

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return (
    err.message.includes('Failed to fetch dynamically imported module') ||
    err.message.includes('Importing a module script failed') ||
    err.name === 'ChunkLoadError'
  );
}

export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err)) {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG);
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          // Suspend indefinitely while reload happens — the promise never resolves
          return new Promise(() => {});
        }
      }
      throw err;
    }
  });
}
